/**
 * Brand metrics display card with visual progress bars
 */

import { Card } from '@/components/ui/Card';
import { colors } from '@/lib/design-tokens';
import type { BrandMetrics } from '@/types';

export interface BrandMetricsCardProps {
  metrics: BrandMetrics;
  videoDuration?: number; // Optional: to show percentage
}

export function BrandMetricsCard({ metrics, videoDuration = 15 }: BrandMetricsCardProps) {
  // Guard against placeholder durations (0.01s used for Meta imports before analysis)
  const isValidDuration = typeof videoDuration === 'number' && videoDuration >= 1;

  // Calculate percentages only when duration is valid, cap at 100%
  const logoPercentage = isValidDuration
    ? Math.min((metrics.logoDurationSeconds / videoDuration) * 100, 100)
    : null;
  const productPercentage = isValidDuration
    ? Math.min((metrics.productDurationSeconds / videoDuration) * 100, 100)
    : null;

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-6">Brand Metrics</h3>

      <div className="space-y-6">
        {/* Logo Duration */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Logo Duration</span>
            <span className="text-sm font-bold text-gray-900">
              {metrics.logoDurationSeconds.toFixed(1)}s
              <span className="text-gray-500 font-normal ml-1">
                {logoPercentage !== null ? `(${logoPercentage.toFixed(0)}%)` : '(Processing...)'}
              </span>
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${logoPercentage !== null ? logoPercentage : 0}%`,
                backgroundColor: colors.status.success,
              }}
            />
          </div>
        </div>

        {/* Product Duration */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Product Duration</span>
            <span className="text-sm font-bold text-gray-900">
              {metrics.productDurationSeconds.toFixed(1)}s
              <span className="text-gray-500 font-normal ml-1">
                {productPercentage !== null ? `(${productPercentage.toFixed(0)}%)` : '(Processing...)'}
              </span>
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${productPercentage !== null ? productPercentage : 0}%`,
                backgroundColor: colors.brand.primary,
              }}
            />
          </div>
        </div>

        {/* Brand Cue by 2s */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-700">Brand Cue by 2s</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Critical for platform algorithms
            </div>
          </div>
          <div
            className="px-4 py-2 rounded-full font-bold text-sm uppercase"
            style={{
              backgroundColor: metrics.brandCueBy2s
                ? colors.status.successSoft
                : colors.status.dangerSoft,
              color: metrics.brandCueBy2s
                ? colors.status.success
                : colors.status.danger,
            }}
          >
            {metrics.brandCueBy2s ? 'Yes' : 'No'}
          </div>
        </div>

        {/* Time to First Brand Cue */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Time to First Brand Cue
          </span>
          <span className="text-sm font-bold font-mono text-gray-900">
            {metrics.timeToFirstBrandCue || (
              <span className="text-gray-400 font-normal">Not detected</span>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
}
